import Vue from "vue";
import { publicApiHost } from "@/constants/urls.js";

// The offer to try the new dashboard: the banner and, once the banner is
// dismissed, the link in the profile menu.
//
// It is opened to every organization user by the global switch, the public
// `dashboard.default` setting. Until the switch points at the new dashboard,
// and whenever the endpoint is missing or fails, the banner stays what it was
// before the switch existed: shown to Charidy staff only, as an internal beta.
//
// Remove together with the endpoint once this dashboard is retired.

const DASHBOARD_DEFAULT_URL = `${publicApiHost}/api/v1/dashboard/default`;
const DASHBOARD_PREFERENCE_URL = `${publicApiHost}/api/v1/dashboard/preference`;
const DASHBOARD_DEFAULT_TIMEOUT_MS = 3000;
const DISMISSED_KEY = "new_dashboard_banner_dismissed";
const FRESH_SIGN_IN_KEY = "dashboard_fresh_sign_in";
const PRODUCTION_URL = "https://dashboard2.charidy.com/";
const SUBDOMAIN_LABEL = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;

export const newDashboardOffer = Vue.observable({
  // False until the switch has been asked, so nothing is drawn and then redrawn.
  resolved: false,
  switchedOn: false,
  url: PRODUCTION_URL,
  dismissed: localStorage.getItem(DISMISSED_KEY) === "1",
});

// The new dashboard next to this one: the same address with the first label
// swapped. On any other host (a local run) the production address is used.
function siblingDashboardUrl(legacy, v2) {
  const { hostname } = window.location;
  const prefix = `${legacy}.`;
  if (!hostname.startsWith(prefix)) return PRODUCTION_URL;
  return `https://${v2}.${hostname.slice(prefix.length)}/`;
}

// The `dashboard.default` answer, or null without a usable one. With a login's
// token it also carries `preference`, that login's organization's own choice.
async function fetchDashboards(token) {
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), DASHBOARD_DEFAULT_TIMEOUT_MS);
  try {
    const response = await fetch(DASHBOARD_DEFAULT_URL, {
      signal: abort.signal,
      ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
    });
    if (!response.ok) return null;
    const dashboards = (await response.json())?.data;
    const labels = [dashboards?.legacy, dashboards?.v2];
    if (!labels.every((label) => typeof label === "string" && SUBDOMAIN_LABEL.test(label))) {
      return null;
    }
    return dashboards;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

async function fetchSwitchedOnUrl() {
  const dashboards = await fetchDashboards();
  if (dashboards?.default !== "v2") return null;
  return siblingDashboardUrl(dashboards.legacy, dashboards.v2);
}

let loading = null;

// Asks the switch once per page load, whoever calls first.
export function loadNewDashboardOffer() {
  if (!loading) {
    loading = fetchSwitchedOnUrl().then((url) => {
      if (url) {
        newDashboardOffer.switchedOn = true;
        newDashboardOffer.url = url;
      }
      newDashboardOffer.resolved = true;
    });
  }
  return loading;
}

export function dismissNewDashboardOffer() {
  newDashboardOffer.dismissed = true;
  localStorage.setItem(DISMISSED_KEY, "1");
}

// Remembers that the organization moved to the new dashboard, so its next login
// lands there whatever the switch says. The new dashboard opens in a tab of its
// own while this one stays, so nothing cuts the request short. Best effort: the
// choice is saved again on the next switch.
export function rememberNewDashboard(organizationId) {
  const token = localStorage.getItem("jwt_token");
  if (!token || !(organizationId > 0)) return Promise.resolve();
  return fetch(DASHBOARD_PREFERENCE_URL, {
    method: "PUT",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify({ preference: "v2", organization_id: organizationId }),
  }).catch(() => {});
}

// A sign-in just stored its session (the `setUser` mutation). Kept in this tab
// across the reload some logins end with, until the router has decided on the
// first page after it.
export function markFreshSignIn() {
  sessionStorage.setItem(FRESH_SIGN_IN_KEY, "1");
}

export function isFreshSignIn() {
  return sessionStorage.getItem(FRESH_SIGN_IN_KEY) === "1";
}

export function forgetFreshSignIn() {
  sessionStorage.removeItem(FRESH_SIGN_IN_KEY);
}

// Right after a sign-in: an organization that chose the new dashboard
// (`dashboard_preference` = v2) goes there instead of into this one. Only that
// choice counts, not the switch: with no choice the person stays where they
// came. The new dashboard takes the session over from the shared cookie
// `setUser` wrote. Resolves true when the page is leaving; no answer keeps them.
export async function leaveForChosenNewDashboard() {
  const token = localStorage.getItem("jwt_token");
  if (!token) return false;
  const dashboards = await fetchDashboards(token);
  if (dashboards?.preference !== "v2") return false;
  window.location.assign(siblingDashboardUrl(dashboards.legacy, dashboards.v2));
  return true;
}
